import React, { useState } from 'react';
import './CallReasons.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { TreeView } from '@mui/x-tree-view/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { matchSorter } from 'match-sorter';
import FormGroup from '@mui/material/FormGroup';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

interface IInput {
  key: string,
  handleChange: (textValue: string) => void,
  handleCallReasonChange: (callReasonValue: string) => void,
  id: string,
  callReasons: string[],
  surveyCallReasonValues: string[],
  addNotesList: string[],
  expanded: string[],
  handleToggle: (event: React.ChangeEvent<HTMLInputElement>, nodeIds: string[]) => any,
  handleSelect: (event: React.ChangeEvent<HTMLInputElement>, path: string) => void,
  theme: any,
  onRemoveBtnClick: (id: string) => void,
  renderTree: (nodes: any) => any,
  findChildMatches: (parentPath: string, array: any[], textToMatch: string) => string[],
} 

interface CallReasonsProps {
  contactAttributes: {
    typeId: string;
    msgType?: string;
    absenceBeginTime?: string;
  };
  surveyCallReasonValues: string[];
  handleCallReasonChange: (callReasonValue: string) => void;
  calls?: {
    array_of_sub_options?: {
      value: any[];
    };
  };
  chats?: {
    array_of_sub_options?: {
      value: any[];
    };
  };
  failureTask?: {
    array_of_sub_options?: {
      value: any[];
    };
  };
  callbackTask?: {
    array_of_sub_options?: {
      value: any[];
    };
  };
  intermittentAbsenceTask?: {
    array_of_sub_options?: {
      value: any[];
    };
  };
  primaryCallReason: string;
  handlePrimaryCallReasonChange: (selectedPrimaryCallReason: string) => void;
  inputList: string[];
  addNotesList: string[];
  setAddNotesList: React.Dispatch<React.SetStateAction<string[]>>;
  setInputList: React.Dispatch<React.SetStateAction<string[]>>;
  selected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
  removeCallReason: (id: string) => void;
  notes: string;
  setNotes: React.Dispatch<React.SetStateAction<string>>;
  next: (e: React.MouseEvent<HTMLButtonElement>) => void;
  back: (e: React.MouseEvent<HTMLButtonElement>) => void;
  removeAdditionalNote: (id: string) => void;
  z?: any;
}

const Input = (props: IInput) => {

  const [expandedSelection, setExpandedSelection] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<string[]>([]);
  const [otherReason, setOtherReason] = useState('');

  React.useEffect(() => {
    setSelectedReason(props.surveyCallReasonValues.find(element => element.includes(props.id))?.substring(6,));
  }, [props.surveyCallReasonValues])

  if (selectedReason !== null) {
    React.useEffect(() => {
      setOtherReason(props.addNotesList.find(element => element.includes(props.id)).substring(6,));
    }, [props.addNotesList])
  }
  
  const addHandleToggle = (event: React.ChangeEvent<HTMLInputElement>, nodeIds: string[]) => {
    setExpandedSelection(nodeIds);
  };

  const addHandleSelect = (event: React.ChangeEvent<HTMLInputElement>, path: string) => {
    props.handleCallReasonChange(props.id + path);
  };

  const handleAdditionalNotesChange = (event: any) => {
    setOtherReason(event.target.value)
    props.addNotesList[props.addNotesList.findIndex(element => element.includes(`${props.id}`))] = props.id + event.target.value;
  }

  const filterOptions = (callReasons: string[], { inputValue }) => matchSorter(callReasons, `${inputValue}`, {sorter: rankedItems => rankedItems, keys: [{threshold: matchSorter.rankings.CONTAINS, key: '*.children.*.children.*.path'}, {threshold: matchSorter.rankings.CONTAINS, key: '*.children.*.path'}, {threshold: matchSorter.rankings.CONTAINS, key: '*.path'}]});

  return (
    <div>
      <Autocomplete
        value={selectedReason}
        disablePortal
        className='combobox'
        id={props.id}
        options={props.callReasons}
        filterOptions={filterOptions}
        getOptionName={(option) => option.name}
        renderOption={(props123, option, state) => {
          const expanded = [];
          const textToMatch = state.inputValue.toLowerCase();
          if(option.name.toLowerCase().includes(textToMatch) && textToMatch !== '') {
            expanded.push(option.path);
          }

          if(option.children.length > 0 && textToMatch !== '') {
            const childMatches = props.findChildMatches(option.path, option.children, textToMatch);
            //If matches found in child arrays we want to expand parent
            if(childMatches.length > 0) {
              expanded.push(option.path);
            }
            expanded.push(...childMatches);
          }
          expanded.push(...expandedSelection);

          const matches = match(option.name, state.inputValue.toLowerCase(), { insideWords: true });
          const parts = parse(option.name, matches);
          
          return (
            <TreeView
              id={props.id}
              aria-label="file system navigator"
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              expanded={expanded}
              selected={selectedReason}
              onNodeToggle={addHandleToggle}
              onNodeSelect={addHandleSelect}
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
              }}
            >
              {props.renderTree(option, state.inputValue)}
            </TreeView>
          );
            }}
          renderInput={(params) =>
            <TextField 
              id={props.id}
              {...params} 
              label="Select a reason" 
              inputProps={{
                ...params.inputProps,
              }}
            />}            
        />
        {selectedReason !== undefined && selectedReason.includes('Other' || 'other')
            ?<div
              id="acc"
             >
              <FormGroup sx={{ m: 3 }} component="fieldset" variant="standard" className="fs-unmask">
                <TextField
                  id={props.id}
                  name="additionalotes"
                  label="Provide a reason"
                  variant="outlined"
                  multiline
                  rows={1}
                  inputProps={{maxLength: 200}}
                  onChange={handleAdditionalNotesChange}
                  value={otherReason}
                  className="fs-unmask"
                  fullWidth

                />
              </FormGroup>
             </div>
            :null}
      <div id="removeReasonDiv">
        <p className="removeHeading fs-unmask">
          <ThemeProvider theme={props.theme}>
            <Button 
              color="primary"
              sx={{
                fontWeight: 'bold',
                fontSize: 14,
                //ml: 30,
              }}
              onClick={() => props.onRemoveBtnClick(props.id)}
            >
              Remove reason
            </Button>
          </ThemeProvider>
        </p>
        <hr className="hr" />
      </div>
    </div>
  )
}

const CallReasons: React.FC<CallReasonsProps> = (props) => {

  const [error, setError] = useState(false);


// changes color on the additional reasons button
  const theme = createTheme({
    palette: {
      primary: {
        main: '#127CB7',
      },
    },
  });

  const [primSelected, setPrimSelected] = useState([]);
  const [expandedSelection, setExpandedSelection] = useState([]);

  const handleToggle = (event, nodeIds) => {
    setExpandedSelection(nodeIds);
  };

  const handleSelect = (event, path) => {
    setPrimSelected(path);
    props.handlePrimaryCallReasonChange(path);
    console.log(path);
  };

    const mapObject = (reasons) => {
      if (props.contactAttributes.typeId === 'Chat') {
        if (!props.chats || props.chats.array_of_sub_options.value.length < 0) return [];
        
        return reasons.map(obj => { return {
          id: obj.system.id,
          name: obj.system.name,
          path: obj.parent_option_name.value,
          children: mapObject(obj.array_of_sub_options.value)
        }});
      }
      else if (props.contactAttributes.typeId === 'Task' && 
        !props.contactAttributes.msgType &&
        !props.contactAttributes.absenceBeginTime) {
        if (!props.callbackTask || props.callbackTask.array_of_sub_options.value.length < 0) return [];
        
        return reasons.map(obj => { return {
          id: obj.system.id,
          name: obj.system.name,
          path: obj.parent_option_name.value,
          children: mapObject(obj.array_of_sub_options.value)
        }});
      }
      else if (props.contactAttributes.typeId === 'Task' && props.contactAttributes.msgType) {
        if (!props.failureTask || props.failureTask.array_of_sub_options.value.length < 0) return [];
        
        return reasons.map(obj => { return {
          id: obj.system.id,
          name: obj.system.name,
          path: obj.parent_option_name.value,
          children: mapObject(obj.array_of_sub_options.value)
        }});
      }
      else if (props.contactAttributes.typeId === 'Task' && props.contactAttributes.absenceBeginTime) {
        if (!props.intermittentAbsenceTask || props.intermittentAbsenceTask.array_of_sub_options.value.length < 0) return [];
        
        return reasons.map(obj => { return {
          id: obj.system.id,
          name: obj.system.name,
          path: obj.parent_option_name.value,
          children: mapObject(obj.array_of_sub_options.value)
        }});
      }
      else {
        if (!props.calls || props.calls.array_of_sub_options.value.length < 0) return [];
        
        return reasons.map((obj: any) => { return {
          id: obj.system.id,
          name: obj.system.name,
          path: obj.parent_option_name.value,
          children: mapObject(obj.array_of_sub_options.value)
        }});
      }
    }

    let callReasons =  {}
    if (props.contactAttributes.typeId === 'Chat') {
      callReasons = mapObject(props.chats?.array_of_sub_options?.value);
    }
    else if (props.contactAttributes.typeId === 'Task' &&
      !props.contactAttributes.msgType &&
      !props.contactAttributes.absenceBeginTime) {
      callReasons = mapObject(props.callbackTask?.array_of_sub_options?.value);
    }
    else if (props.contactAttributes.typeId === 'Task' && props.contactAttributes.msgType) {
      callReasons = mapObject(props.failureTask?.array_of_sub_options?.value);
    }
    else if (props.contactAttributes.typeId === 'Task' && props.contactAttributes.absenceBeginTime) {
      callReasons = mapObject(props.intermittentAbsenceTask?.array_of_sub_options?.value);
    }
    else {
      callReasons = mapObject(props.calls?.array_of_sub_options?.value);
    }
    callReasons
    
    const renderTree = (nodes, inputValue) => {
      if (inputValue === '' || inputValue === undefined) {
        return (
        <TreeItem key={nodes.id} nodeId={nodes.path} label={nodes.name}>
          {Array.isArray(nodes.children)
            ? nodes.children.map((node) => renderTree(node))
            : null}
        </TreeItem>
        );
      } else {
        const matches = match(nodes.name, `${inputValue}`, { insideWords: true });
        const parts = parse(nodes.name, matches);
        return (
          <TreeItem 
            key={nodes.id} 
            nodeId={nodes.path} 
            label={<li>
                  {parts.map((part) => (
                    <span
                      key={part.text}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </li>}>
          {Array.isArray(nodes.children)
            ? nodes.children.map((node) => renderTree(node, inputValue))
            : null}
        </TreeItem>
        );
      }
    }
//parts[0].highlight === 'false' ? nodes.name : nodes.name.replace(`${props.part.text}`, `${props.part.text}`)
    const handleChange = textValue => {
        props.handleCallReasonChange(textValue);
    };

    const onRemoveBtnClick = (id)=> {
      props.setInputList(props.inputList.filter(q => q !== id));
      props.removeCallReason(id);
      props.removeAdditionalNote(id);
    }

    const onAddBtnClick = event => {
      const id = "id" + Math.random().toString(36).substring(2, 6);
      props.setInputList([...props.inputList, id]);
      props.setAddNotesList([...props.addNotesList, id]);
      props.addNotesList;
    };

    const nextClicked = e => {
      let errorFound = false;
  
      if (
        props.primaryCallReason == ''
      ) {
        errorFound = true;
        setError(true);
      }
  
      if (!errorFound) {
        setError(false);
        props.next(e);
      }
    };

    const filterOptions = (callReasons, { inputValue }) => matchSorter(callReasons, `${inputValue}`, {sorter: rankedItems => rankedItems, keys: [{threshold: matchSorter.rankings.CONTAINS, key: '*.children.*.children.*.path'}, {threshold: matchSorter.rankings.CONTAINS, key: '*.children.*.path'}, {threshold: matchSorter.rankings.CONTAINS, key: '*.path'}]});

    const handleNotesChange = event => {
      props.setNotes(event.target.value);
    };

    const findChildMatches = (parentPath, array, textToMatch) => {
      const arr = [];
      array.map(o => {
        if(o.name.toLowerCase().includes(textToMatch) && textToMatch !== '') {
          arr.push(parentPath);
          arr.push(o.path);
        }
        if(o.children.length > 0 && textToMatch !== '') {
          arr.push(...findChildMatches(o.path, o.children, textToMatch));
        }
      })
      return arr;
    }
    
    return (
        <div id="callReasonsDiv">
        <div id="pageTitle">
          <p className="surveyHeading fs-unmask">
            {props.contactAttributes.typeId === 'Chat'
              ? 'CHAT REASONS'
              : props.contactAttributes.typeId === 'Task'
              ? 'TASK REASONS'
              : 'CALL REASONS'}
          </p>
          <p className="surveyInfo fs-unmask">
            First add a primary reason, followed by any additional reasons.
          </p>
          <hr className='hr' />
          </div>
          <div id="primaryReasonDiv">
            <p className="primaryHeading fs-unmask">
              PRIMARY REASON
            </p>
            <Autocomplete
              value={props.primaryCallReason}
              disablePortal
              className='combobox'
              id="combo-box-demo"
              options={callReasons}
              getOptionName={(option) => option.name}
              filterOptions={filterOptions}
              renderOption={(props123, option, state) => {
                const expanded = [];
                const textToMatch = state.inputValue.toLowerCase();
                if(option.name.toLowerCase().includes(textToMatch) && textToMatch !== '') {
                  expanded.push(option.path);
                }

                if(option.children.length > 0 && textToMatch !== '') {
                  const childMatches = findChildMatches(option.path, option.children, textToMatch);
                  //If matches found in child arrays we want to expand parent
                  if(childMatches.length > 0) {
                    expanded.push(option.path);
                  }
                  expanded.push(...childMatches);
                }
                expanded.push(...expandedSelection);

                return (
                  <TreeView
                    aria-label="file system navigator"
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={expanded}
                    selected={primSelected}
                    onNodeToggle={handleToggle}
                    onNodeSelect={handleSelect}
                    sx={{
                      flexGrow: 1,
                      overflowY: 'auto',
                    }}
                  >
                    {renderTree(option, state.inputValue)}
                  </TreeView>
                );
              }}
              renderInput={(params) =>
                <TextField 
                  {...params} 
                  label="Select a reason" 
                  inputProps={{
                    ...params.inputProps,
                  }}
                  />}            
            />{props.primaryCallReason.toLowerCase().includes('other')
            ?<div
              id="acc"
             >
              <FormGroup sx={{ m: 3 }} component="fieldset" variant="standard" className="fs-unmask">
                <TextField
                  id="notes"
                  name="notes"
                  label="Provide a reason"
                  variant="outlined"
                  multiline
                  rows={1}
                  inputProps={{maxLength: 200}}
                  onChange={handleNotesChange}
                  value={props.notes}
                  className="fs-unmask"
                  fullWidth

                />
              </FormGroup>
             </div>
            :null}
          </div>
          <hr className="hr" />
          <div id="additionalReasonsDiv">
            <p className="additionalHeading fs-unmask">
              ADDITIONAL REASON(S) 
            <ThemeProvider theme={theme}>
              <Button 
                color="primary"
                sx={{
                  fontWeight: 'bold',
                  fontSize: 14,
                  ml: 30,
                }}
                onClick={onAddBtnClick}
              >
                Add a Reason
              </Button>
            </ThemeProvider>
            {props.inputList.map(additionalReasonId => {
              return(
                <Input 
                  key={additionalReasonId}
                  handleChange={handleChange}
                  handleCallReasonChange={props.handleCallReasonChange}
                  id={additionalReasonId} 
                  //value={props.selected}
                  callReasons={callReasons} 
                  surveyCallReasonValues={props.surveyCallReasonValues} 
                  expanded={expandedSelection} 
                  handleToggle={handleToggle} 
                  handleSelect={handleSelect} 
                  theme={theme} 
                  onRemoveBtnClick={onRemoveBtnClick} 
                  renderTree={renderTree}
                  // surveyAdditionalNotesValues={props.surveyAdditionalNotesValues}
                  addNotesList={props.addNotesList}
                  setAddNotesList={props.setAddNotesList}
                  z={props.z}
                  findChildMatches={findChildMatches}
                />);
            })}
            </p>
          </div> 
            <div
              id="buttonContainer"
              style={{ background: 'white' }}
              class="surveyButtonContainer"
            >
            <hr className="hr" />
            <Stack spacing={2} direction="row">
              <Button
                id="backButton"
                variant="outlined"
                style={{
                  borderRadius: '20px',
                  opacity: 1,
                  marginBottom: '10px',
                  width: '40%',
                  left: '10%',
                }}
                onClick={props.back}
                className="fs-unmask"
              >
                Back
              </Button>
              <Button
                id="nextButton"
                variant="contained"
                style={{
                  background: '#127CB7',
                  borderRadius: '20px',
                  opacity: 1,
                  marginBottom: '10px',
                  width: '40%',
                  left: '10%',
                }}
                onClick={nextClicked}
                className="fs-unmask"
              >
                Next
              </Button>
            </Stack>
          </div>
      </div>
    );
};

export default CallReasons;
